import type { Signal } from "@suse-o11y/signal";
import { notification as antdNotification } from "antd/es";
import type { History } from "history";
import React from "react";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import type { Store } from "redux";

import type { RootState } from "./app/state/RootState";
import { PinnedItemsProvider, type PinnedItemsState } from "./bottom_content/view/panes/east/pinned-items";
import { SouthPaneProvider } from "./bottom_content/view/panes/south/south-pane-provider";
import type { SouthPaneState } from "./bottom_content/view/panes/south/use-south-pane-state";
import { WebSocketProvider } from "./components/_websocket";
import { BrowserPageTitleProvider } from "./components/browser-page-title";
import { ToggleListStateProvider } from "./components/react/_toggle-list";
import { setNotificationInstance } from "./components/react/notification/Notification";
import { TimeRangeHistoryProvider } from "./components/react/timeline/TimeRangeHistory";
import type { TimeRangeHistory } from "./components/react/timeline/time-range-history-utils";
import { RootApplcationDomainProvider } from "./components/root-application-domain-provider";
import { TimestampCursorProvider } from "./components/TimestampCursor";
import { DashboardProvider } from "./routes/dashboards/dashboard/provider";

export const ReduxStoreProvider: React.FC<React.PropsWithChildren<{ store: Store<RootState> }>> = (props) => (
	<Provider {...props} />
);

export const RouterProvider: React.FC<React.PropsWithChildren<{ history: History }>> = (props) => <Router {...props} />;

export interface ProvidersProps {
	store: Store<RootState>;
	history: History;
	pinnedItemsState?: PinnedItemsState;
	timeRangeHistory?: Signal<TimeRangeHistory>;
	southPane?: SouthPaneState;
}

export const Providers: React.FC<React.PropsWithChildren<ProvidersProps>> = ({
	store,
	history,
	pinnedItemsState,
	timeRangeHistory,
	southPane,
	children,
}) => {
	const [notificationApi, notificationContextHolder] = antdNotification.useNotification();

	/**
	 * An ugly workaround to mount the notifications context holder into our Providers tree.
	 * This way notifications have access to project context and can be used in any component
	 * without having to pass the notification instance down the tree.
	 */
	React.useInsertionEffect(() => {
		setNotificationInstance(notificationApi);
	}, [notificationApi]);

	return (
		<ReduxStoreProvider store={store}>
			<WebSocketProvider>
				<RouterProvider history={history}>
					<RootApplcationDomainProvider>
						<BrowserPageTitleProvider>
							<TimestampCursorProvider>
								<TimeRangeHistoryProvider history={timeRangeHistory}>
									<PinnedItemsProvider initialState={pinnedItemsState}>
										<ToggleListStateProvider>
											<SouthPaneProvider state={southPane}>
												<DashboardProvider>
													{notificationContextHolder}
													{children}
												</DashboardProvider>
											</SouthPaneProvider>
										</ToggleListStateProvider>
									</PinnedItemsProvider>
								</TimeRangeHistoryProvider>
							</TimestampCursorProvider>
						</BrowserPageTitleProvider>
					</RootApplcationDomainProvider>
				</RouterProvider>
			</WebSocketProvider>
		</ReduxStoreProvider>
	);
};
